import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  IconButton,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Tooltip,
  Paper,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { getConfig } from '../services/config';
import { REQUEST_HEADERS } from '../constants/headers';

export interface CodeSnippetsModalProps {
  open: boolean;
  onClose: () => void;
  functionName: string;
  endpoint: string;
  inputTypes: string[];
  allowedModels: string[];
  metadata?: any;
  embedded?: boolean;
  maxThreadLength?: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`code-tabpanel-${index}`}
      aria-labelledby={`code-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const inputTypeExamples: Record<string, { description: string; example: any }> = {
  text: {
    description: 'Plain text input',
    example: 'Analyze this text and extract key information'
  },
  json: {
    description: 'JSON structured data',
    example: { name: 'John Doe', age: 30, email: 'john@example.com' }
  },
  image: {
    description: 'Image file (base64 encoded)',
    example: 'data:image/jpeg;base64,/9j/4AAQSkZJRg...'
  },
  audio: {
    description: 'Audio file (base64 encoded)',
    example: 'data:audio/mp3;base64,SUQzBAAAAAA...'
  },
  video: {
    description: 'Video file (base64 encoded)',
    example: 'data:video/mp4;base64,AAAAGGZ0eXBt...'
  },
  pdf: {
    description: 'PDF file (base64 encoded)',
    example: 'data:application/pdf;base64,JVBERi0xLj...'
  },
  file: {
    description: 'Any file type (base64 encoded)',
    example: 'data:application/octet-stream;base64,RmlsZSBjb250ZW50cy4uLg=='
  },
  csv: {
    description: 'CSV data as text',
    example: 'Name,Age,Email\nJohn Doe,30,john@example.com\nJane Smith,25,jane@example.com'
  }
};

export const CodeSnippetsModal: React.FC<CodeSnippetsModalProps> = ({
  open,
  onClose,
  functionName,
  endpoint,
  inputTypes,
  allowedModels,
  metadata,
  embedded = false,
  maxThreadLength = 50,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [selectedInputType, setSelectedInputType] = useState(inputTypes[0] || 'text');
  const [selectedModel, setSelectedModel] = useState(allowedModels[0]);
  const [copyAlert, setCopyAlert] = useState(false);
  const [threadMode, setThreadMode] = useState(false);
  const [formDataMode, setFormDataMode] = useState(false);
  const [vectorSearchEnabled, setVectorSearchEnabled] = useState(false);
  const [categories, setCategories] = useState<Array<{id: string, name: string}>>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const { user } = useAuth();
  const [apiKeys, setApiKeys] = useState<{ id: number; name?: string; api_key?: string }[]>([]);
  const [selectedApiKey, setSelectedApiKey] = useState<string>('YOUR_AUTH_TOKEN');

  const config = getConfig();
  let baseUrl = config.apiBaseUrl;
  // Remove trailing slash from baseUrl if present
  if (baseUrl.endsWith('/')) {
    baseUrl = baseUrl.slice(0, -1);
  }
  // endpoint already includes /api/v2/functions/... so just append it
  const fullEndpoint = `${baseUrl}${endpoint}${formDataMode ? '/form' : ''}`;
  const inputInfo = inputTypeExamples[selectedInputType] || inputTypeExamples.text;

  useEffect(() => {
    const loadKeys = async () => {
      try {
        const idToken = await user?.getIdToken();
        if (!idToken) return;
        const res = await fetch('/api/v2/api-keys/', {
          headers: { Authorization: `Bearer ${idToken}` },
        });
        if (!res.ok) return;
        const response = await res.json();
        const keys = response.data || [];
        setApiKeys(keys);
        if (keys.length > 0) {
          setSelectedApiKey(keys[0].api_key || 'YOUR_AUTH_TOKEN');
        }
      } catch {}
    };
    if (open) loadKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Fetch categories when component mounts
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const idToken = await user?.getIdToken();
        if (!idToken) return;
        
        // Extract customer tag from endpoint (format: /api/v2/functions/{customerTag}/{functionName})
        const pathParts = endpoint.split('/');
        const customerTagIndex = pathParts.findIndex(p => p === 'functions') + 1;
        const customerTag = pathParts[customerTagIndex];
        
        if (!customerTag) return;
        
        const response = await fetch(`/api/v2/customers/${customerTag}/categories`, {
          headers: {
            'Authorization': `Bearer ${idToken}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setCategories(data.data?.items || []);
        }
      } catch (error) {

      }
    };
    
    if (open && user) {
      fetchCategories();
    }
  }, [open, user, endpoint]);

  // Auto-apply metadata settings when component opens
  useEffect(() => {
    if (open && metadata) {
      console.log('CodeSnippetsModal applying metadata:', metadata);
      const isChatbot = metadata.chatbot === true;
      const hasCategories = !!(metadata.knowledge_categories && Array.isArray(metadata.knowledge_categories) && metadata.knowledge_categories.length > 0);
      
      console.log('Setting threadMode to:', isChatbot);
      console.log('Setting vectorSearchEnabled to:', hasCategories);
      
      setThreadMode(isChatbot);
      setVectorSearchEnabled(hasCategories);
      
      if (hasCategories && metadata.knowledge_categories) {
        console.log('Setting selected categories:', metadata.knowledge_categories);
        setSelectedCategories(metadata.knowledge_categories as string[]);
      }
    }
  }, [open, metadata]);

  // Create request body based on input type
  const getRequestBody = () => {
    if (formDataMode) {
      // Form data mode - return field descriptions
      return {
        _fields: "Form fields to send:",
        input: "File or text content (required)",
        input1: "(Optional) Second file or text",
        input2: "(Optional) Third file or text",
        input_type: selectedInputType,
        model: selectedModel,
        message: "(Optional) Instructions or context",
        metadata: '{"source": "api", "version": "1.0"}'
      };
    }

    const base = {
      input_type: selectedInputType,
      model: selectedModel
    };

    if (['image', 'audio', 'video', 'pdf', 'file'].includes(selectedInputType)) {
      const result: any = {
        ...base,
        input: inputInfo.example,
        metadata: {
          filename: `example.${selectedInputType === 'pdf' ? 'pdf' : selectedInputType}`,
          content_type: `${selectedInputType === 'pdf' ? 'application' : selectedInputType}/${selectedInputType === 'pdf' ? 'pdf' : selectedInputType}`
        }
      };
      
      // Add vector search metadata if enabled
      if (vectorSearchEnabled && selectedCategories.length > 0) {
        result.metadata.vector_search = {
          enabled: true,
          category_ids: selectedCategories,
          top_k: 8
        };
      }
      
      return result;
    } else {
      const result: any = {
        ...base,
        input: inputInfo.example
      };
      
      // Add vector search metadata if enabled
      if (vectorSearchEnabled && selectedCategories.length > 0) {
        result.metadata = {
          vector_search: {
            enabled: true,
            category_ids: selectedCategories,
            top_k: 8
          }
        };
      }
      
      return result;
    }
  };

  const requestBody = getRequestBody();

  const handleCopy = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopyAlert(true);
    setTimeout(() => setCopyAlert(false), 2000);
  };

  const generateCurlCode = () => {
    if (formDataMode) {
      const lines = [];
      lines.push('# Form data mode - supports multiple file uploads');
      lines.push('# Replace FILE PATHS with your actual files!');
      lines.push('');
      lines.push('curl -X POST "' + fullEndpoint + '" \\');
      lines.push('  -H "Authorization: Bearer ' + selectedApiKey + '" \\');
      if (threadMode) {
        lines.push('  -H "EASYAI-THREAD-ID: your-unique-thread-id" \\');
        lines.push('  -H "EASYAI-MAX-THREAD-LEN: 10" \\');
      }
      lines.push('  -F "input=@/path/to/your/file.' + selectedInputType + '" \\');
      lines.push('  -F "input_type=' + selectedInputType + '" \\');
      lines.push('  -F "model=' + selectedModel + '" \\');
      lines.push('  -F "message=Describe what you want done with the file(s)" \\');
      lines.push('  -F \'metadata={"source": "api", "version": "1.0"}\'')
      lines.push('');
      lines.push('# Examples with real paths:');
      lines.push('# -F "input=@/Users/yourname/Downloads/image.jpg"');
      lines.push('# -F "input=@./myfile.pdf"  # relative path');
      lines.push('# -F "input=@~/Documents/file.' + selectedInputType + '"  # home directory');
      lines.push('');
      lines.push('# To add more files (use different field names):');
      lines.push('# -F "input1=@/path/to/second/file.' + selectedInputType + '"');
      lines.push('# -F "input2=@/path/to/third/file.' + selectedInputType + '"');
      lines.push('');
      lines.push('# For text input instead of file:');
      lines.push('# -F "input=Your text content here"  # no @ symbol');
      
      return lines.join('\n');
    }
    
    const threadHeaders = threadMode 
      ? `  # === THREAD MODE ENABLED ===
  -H "EASYAI-THREAD-ID: your-unique-thread-id" \\
  -H "EASYAI-MAX-THREAD-LEN: 10" \\` 
      : '';

    if (['image', 'audio', 'video', 'pdf', 'file'].includes(selectedInputType)) {
      // Build metadata string with shell variable interpolation
      const contentType = selectedInputType === 'pdf' ? 'application/pdf' : selectedInputType === 'file' ? 'application/octet-stream' : selectedInputType + '/' + selectedInputType;
      
      let metadataLines = [
        // eslint-disable-next-line no-useless-escape
        `    "filename": "'\$(basename "\$FILE_PATH")'",`,
        `    "content_type": "${contentType}"`
      ];
      
      // Add vector search if enabled
      if (vectorSearchEnabled && selectedCategories.length > 0) {
        metadataLines[metadataLines.length - 1] += ','; // Add comma to previous line
        metadataLines.push('    "vector_search": {');
        metadataLines.push('      "enabled": true,');
        metadataLines.push(`      "category_ids": ${JSON.stringify(selectedCategories)},`);
        metadataLines.push('      "top_k": 8');
        metadataLines.push('    }');
      }
      
      const metadataStr = '{\n' + metadataLines.join('\n') + '\n  }';
      
      return `# For ${selectedInputType} files, encode to base64 and create data URL
# macOS/Linux example:
FILE_PATH="yourfile.${selectedInputType}"  # <- REPLACE WITH YOUR FILE
BASE64_DATA=$(base64 -i "$FILE_PATH" | tr -d '\n')
DATA_URL="data:${contentType};base64,$BASE64_DATA"

curl -X POST "${fullEndpoint}" \\
  -H "Authorization: Bearer ${selectedApiKey}" \\
  -H "Content-Type: application/json" \\${threadMode ? '\n' + threadHeaders : ''}
  -d '{
  "input_type": "${selectedInputType}",
  "model": "${selectedModel}",
  "input": '"$DATA_URL"',
  "metadata": ${metadataStr}
}'
# Note: For large files (>10MB), the API will automatically use GCS for efficiency.`;
    }
    
    const formattedBody = JSON.stringify(requestBody, null, 2)
      .split('\n')
      .map(line => {
        if (line.includes('"input":')) {
          return line.replace(/"input": ".*?"/, `"input": "${inputInfo.example}"`);
        }
        return line;
      })
      .join('\n');
    
    return `curl -X POST "${fullEndpoint}" \\
  -H "Authorization: Bearer ${selectedApiKey}" \\
  -H "Content-Type: application/json" \\${threadMode ? '\n' + threadHeaders : ''}
  -d '${formattedBody}'
# Note: The response will include usage headers:
# - ${REQUEST_HEADERS.USAGE}: Total tokens used (for backward compatibility)
# - ${REQUEST_HEADERS.INPUT_TOKENS}: Input tokens
# - ${REQUEST_HEADERS.OUTPUT_TOKENS}: Output tokens  
# - ${REQUEST_HEADERS.CREDITS_USED}: AI credits consumed`;
  };

  const generatePythonCode = () => {
    const threadHeaders = threadMode 
      ? `    # === THREAD MODE ENABLED: Conversation context will be maintained ===
    "EASYAI-THREAD-ID": "your-unique-thread-id",  # Use same ID across related requests
    "EASYAI-MAX-THREAD-LEN": "10",  # Keep last 10 messages (default: 10, max: ${maxThreadLength})`
      : `    # === OPTIONAL: Enable Thread Mode for conversation context ===
    # "EASYAI-THREAD-ID": "your-unique-thread-id",  # Uncomment to enable
    # "EASYAI-MAX-THREAD-LEN": "10"  # Max history messages (default: 10, max: ${maxThreadLength})`;

    const vectorSearchComment = vectorSearchEnabled && selectedCategories.length > 0
      ? `# === VECTOR SEARCH ENABLED: Knowledge base will augment the request ===
# The following metadata enables automatic retrieval of relevant context
# from your uploaded documents in the selected categories.
`
      : `# === OPTIONAL: Enable Vector Search to augment with knowledge base ===
# Uncomment the metadata block below to search your uploaded documents
# "metadata": {
#   "vector_search": {
#     "enabled": true,
#     "category_ids": ["category-id-1", "category-id-2"],
#     "top_k": 8  # Number of relevant chunks to retrieve
#   }
# }
`;

    if (formDataMode) {
      const pyLines = [];
      pyLines.push('import requests');
      pyLines.push('import json');
      pyLines.push('');
      pyLines.push('# Multiple file upload with FormData');
      pyLines.push('# IMPORTANT: Replace these paths with your actual file paths!');
      pyLines.push('');
      pyLines.push('files = []');
      pyLines.push('');
      pyLines.push('# Single file example:');
      pyLines.push('file_path = "/path/to/your/file.' + selectedInputType + '"  # ← REPLACE WITH YOUR FILE');
      pyLines.push('files.append((\'input\', open(file_path, \'rb\')))');
      pyLines.push('');
      pyLines.push('# Multiple files example (uncomment as needed):');
      pyLines.push('# files.append((\'input1\', open(\'/path/to/second/file.' + selectedInputType + '\', \'rb\')))');
      pyLines.push('# files.append((\'input2\', open(\'/path/to/third/file.' + selectedInputType + '\', \'rb\')))');
      pyLines.push('');
      pyLines.push('# Form data');
      pyLines.push('data = {');
      pyLines.push('    \'input_type\': \'' + selectedInputType + '\',');
      pyLines.push('    \'model\': \'' + selectedModel + '\',');
      pyLines.push('    \'message\': \'Describe what you want done with the file(s)\',');
      pyLines.push('    \'metadata\': json.dumps({"source": "api", "version": "1.0"})');
      pyLines.push('}');
      pyLines.push('');
      pyLines.push('headers = {');
      pyLines.push('    "Authorization": "Bearer ' + selectedApiKey + '",');
      if (threadMode) {
        pyLines.push('    "EASYAI-THREAD-ID": "your-unique-thread-id",  # Thread ID for conversation context');
        pyLines.push(`    "EASYAI-MAX-THREAD-LEN": "10",  # Max history messages (default: 10, max: ${maxThreadLength})`);
      } else {
        pyLines.push('    # "EASYAI-THREAD-ID": "your-unique-thread-id",  # Uncomment to enable thread support');
        pyLines.push(`    # "EASYAI-MAX-THREAD-LEN": "10"  # Max history messages (default: 10, max: ${maxThreadLength})`);
      }
      pyLines.push('}');
      pyLines.push('');
      pyLines.push('try:');
      pyLines.push('    response = requests.post(');
      pyLines.push('        "' + fullEndpoint + '",');
      pyLines.push('        headers=headers,');
      pyLines.push('        files=files,');
      pyLines.push('        data=data');
      pyLines.push('    )');
      pyLines.push('    ');
      pyLines.push('    print(f\'Status: {response.status_code}\')');
      pyLines.push(`    print(f"Input tokens: {response.headers.get("${REQUEST_HEADERS.INPUT_TOKENS}", "N/A")}")`);
      pyLines.push(`    print(f"Output tokens: {response.headers.get("${REQUEST_HEADERS.OUTPUT_TOKENS}", "N/A")}")`);
      pyLines.push(`    print(f"Credits used: {response.headers.get("${REQUEST_HEADERS.CREDITS_USED}", "N/A")}")`);
      pyLines.push('    result = response.json()');
      pyLines.push('    print(\'Result:\', result)');
      pyLines.push('    ');
      pyLines.push('finally:');
      pyLines.push('    # Always close file handles');
      pyLines.push('    for name, file_obj in files:');
      pyLines.push('        file_obj.close()');
      pyLines.push('');
      pyLines.push('# For text input instead of files:');
      pyLines.push('# data[\'input\'] = \'Your text content here\'');
      pyLines.push('# response = requests.post(url, headers=headers, data=data)  # No \'files\' param');
      
      return pyLines.join('\n');
    }

    if (['image', 'audio', 'video', 'pdf', 'file'].includes(selectedInputType)) {
      return `import requests
import base64

# ============= YOUR ${selectedInputType.toUpperCase()} FILE =============
# Replace 'yourfile.${selectedInputType}' with your actual file path
with open('yourfile.${selectedInputType}', 'rb') as file:
    encoded_content = base64.b64encode(file.read()).decode('utf-8')
    file_data = f"data:${selectedInputType === 'pdf' ? 'application' : selectedInputType}/${selectedInputType === 'pdf' ? 'pdf' : selectedInputType};base64,{encoded_content}"

headers = {
    "Authorization": "Bearer ${selectedApiKey}",
    "Content-Type": "application/json",
${threadHeaders}
}

response = requests.post(
    "${fullEndpoint}",
    headers=headers,
    json={
        "input_type": "${selectedInputType}",
        "model": "${selectedModel}",
        "input": file_data,  # <-- YOUR ${selectedInputType.toUpperCase()} CONTENT (base64 encoded)
        "metadata": {
            "filename": "yourfile.${selectedInputType}",  # <-- YOUR FILE NAME
            "content_type": "${selectedInputType === 'pdf' ? 'application' : selectedInputType}/${selectedInputType === 'pdf' ? 'pdf' : selectedInputType}"
        }
    }
)

print('Input tokens:', response.headers.get(${REQUEST_HEADERS.INPUT_TOKENS}, 'N/A'))
print('Output tokens:', response.headers.get(${REQUEST_HEADERS.OUTPUT_TOKENS}, 'N/A'))
print('Credits used:', response.headers.get(${REQUEST_HEADERS.CREDITS_USED}, 'N/A'))
result = response.json()
print(result)`;
    }
    
    
    return `import requests

headers = {
    "Authorization": "Bearer ${selectedApiKey}",
    "Content-Type": "application/json",
${threadHeaders}
}

${vectorSearchComment}
# YOUR INPUT DATA
request_body = {
    "input_type": "${selectedInputType}",
    "model": "${selectedModel}",
    "input": "${inputInfo.example}"  # <- YOUR ${selectedInputType.toUpperCase()} INPUT HERE${vectorSearchEnabled && selectedCategories.length > 0 ? `,
    "metadata": {
        "vector_search": {
            "enabled": true,
            "category_ids": ${JSON.stringify(selectedCategories)},  # Your selected categories
            "top_k": 8  # Retrieve top 8 relevant chunks
        }
    }` : ''}
}

response = requests.post(
    "${fullEndpoint}",
    headers=headers,
    json=request_body
)

# Check usage headers
print('Input tokens:', response.headers.get('${REQUEST_HEADERS.INPUT_TOKENS}'))
print('Output tokens:', response.headers.get('${REQUEST_HEADERS.OUTPUT_TOKENS}'))
print('Credits used:', response.headers.get('${REQUEST_HEADERS.CREDITS_USED}'))

result = response.json()
print(result)`;
  };

  const generateJavaScriptCode = () => {
    const threadHeaders = threadMode 
      ? `,
        "EASYAI-THREAD-ID": "your-unique-thread-id",
        "EASYAI-MAX-THREAD-LEN": "10"`
      : '';

    if (['image', 'audio', 'video', 'pdf', 'file'].includes(selectedInputType)) {
      return `// Browser environment - File upload example
// NOTE: This example works for small files (<5MB for video, <3MB for audio, <10MB others)
// For larger files, the system automatically uses GCS upload for efficiency

async function call${functionName.replace(/[^a-zA-Z0-9]/g, '')}() {
  // Get file from input element
  const fileInput = document.getElementById('fileInput'); // <- YOUR FILE INPUT ID
  const file = fileInput.files[0];
  
  if (!file) {
    alert('Please select a file');
    return;
  }
  
  // Convert to base64 data URL (for small files)
  const reader = new FileReader();
  reader.onload = async (e) => {
    const base64Content = e.target.result; // This is already a data URL
    
    const response = await fetch("${fullEndpoint}", {
      method: "POST",
      headers: {
        "Authorization": "Bearer ${selectedApiKey}",
        "Content-Type": "application/json"${threadHeaders}
      },
      body: JSON.stringify({
        input_type: "${selectedInputType}",
        model: "${selectedModel}",
        input: base64Content,
        metadata: {
          filename: file.name,
          file_size: file.size,
          content_type: file.type || "${selectedInputType === 'pdf' ? 'application/pdf' : selectedInputType + '/' + selectedInputType}"
        }
      })
    });
    
    if (!response.ok) {
      throw new Error(\`HTTP error! status: \${response.status}\`);
    }

    const result = await response.json();
    console.log('Result:', result);
  };
  
  reader.readAsDataURL(file);
}

// HTML: <input type="file" id="fileInput" accept="${selectedInputType === 'video' ? 'video/*' : selectedInputType === 'audio' ? 'audio/*' : selectedInputType === 'image' ? 'image/*' : selectedInputType === 'pdf' ? '.pdf' : '*/*'}" />`;
    }
    
    return `async function call${functionName.replace(/[^a-zA-Z0-9]/g, '')}() {
  const requestBody = {
    input_type: "${selectedInputType}",
    model: "${selectedModel}",
    input: "${inputInfo.example}"  // <- YOUR ${selectedInputType.toUpperCase()} INPUT HERE
  };

  const response = await fetch("${fullEndpoint}", {
    method: "POST",
    headers: {
      "Authorization": "Bearer ${selectedApiKey}",
      "Content-Type": "application/json"${threadHeaders}
    },
    body: JSON.stringify(requestBody)
  });
  



  const result = await response.json();

}

call${functionName.replace(/[^a-zA-Z0-9]/g, '')}();`;
  };

  const generateNodeCode = () => {
    const threadHeaders = threadMode 
      ? `,
          "EASYAI-THREAD-ID": "your-unique-thread-id",
          "EASYAI-MAX-THREAD-LEN": "10"`
      : '';

    if (['image', 'audio', 'video', 'pdf', 'file'].includes(selectedInputType)) {
      return `const fs = require('fs');
const axios = require('axios');

// File path - REPLACE WITH YOUR ACTUAL FILE
const filePath = './yourfile.${selectedInputType}';

// Read and encode file
const fileBuffer = fs.readFileSync(filePath);
const base64Content = fileBuffer.toString('base64');
const mimeType = '${selectedInputType === 'pdf' ? 'application/pdf' : selectedInputType === 'file' ? 'application/octet-stream' : selectedInputType + '/' + selectedInputType}';
const fileData = \`data:\${mimeType};base64,\${base64Content}\`;

async function call${functionName.replace(/[^a-zA-Z0-9]/g, '')}() {
  try {
    const response = await axios.post(
      "${fullEndpoint}",
      {
        input_type: "${selectedInputType}",
        model: "${selectedModel}",
        input: fileData,
        metadata: {
          filename: require('path').basename(filePath),
          content_type: mimeType
        }
      },
      {
        headers: {
          "Authorization": "Bearer ${selectedApiKey}",
          "Content-Type": "application/json"${threadHeaders}
        }
      }
    );
    


  } catch (error) {

  }
}

call${functionName.replace(/[^a-zA-Z0-9]/g, '')}();`;
    }
    
    return `const axios = require('axios');

async function call${functionName.replace(/[^a-zA-Z0-9]/g, '')}() {
  try {
    const requestBody = {
      input_type: "${selectedInputType}",
      model: "${selectedModel}",
      input: "${inputInfo.example}"  // <- YOUR ${selectedInputType.toUpperCase()} INPUT HERE
    };

    const response = await axios.post(
      "${fullEndpoint}",
      requestBody,
      {
        headers: {
          "Authorization": "Bearer ${selectedApiKey}",
          "Content-Type": "application/json"${threadHeaders}
        }
      }
    );
    


  } catch (error) {

  }
}

call${functionName.replace(/[^a-zA-Z0-9]/g, '')}();`;
  };

  const codeExamples = [
    { label: 'cURL', code: generateCurlCode() },
    { label: 'Python', code: generatePythonCode() },
    { label: 'JavaScript', code: generateJavaScriptCode() },
    { label: 'Node.js', code: generateNodeCode() },
  ];

  return (
    <>
      {embedded ? (
        <Box sx={{ p: 3 }}>
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '10px'}}>
              <Typography variant="h6">Code Examples: {functionName}</Typography>
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {/* Controls */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={2.5}>
                <FormControl fullWidth size="small">
                  <InputLabel>Input Type</InputLabel>
                  <Select value={selectedInputType} onChange={(e) => setSelectedInputType(e.target.value)} label="Input Type">
                    {inputTypes.map(type => (
                      <MenuItem key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2.5}>
                <FormControl fullWidth size="small">
                  <InputLabel>Model</InputLabel>
                  <Select value={selectedModel} onChange={(e) => setSelectedModel(e.target.value)} label="Model">
                    {allowedModels.map(model => (
                      <MenuItem key={model} value={model}>
                        {model}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>API Key</InputLabel>
                  <Select value={selectedApiKey} onChange={(e) => setSelectedApiKey(e.target.value)} label="API Key">
                    <MenuItem value="YOUR_AUTH_TOKEN">YOUR_AUTH_TOKEN</MenuItem>
                    {apiKeys.map(k => (
                      <MenuItem key={k.id} value={k.api_key || ''}>
                        {(k.name || `Key ${k.id}`) + (k.api_key ? ` — ${k.api_key}` : '')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={threadMode} 
                      onChange={(e) => setThreadMode(e.target.checked)}
                      color="primary"
                      size="small"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <span>Thread Mode</span>
                      {threadMode && <Chip label="ON" size="small" color="primary" sx={{ height: 18, fontSize: '0.7rem' }} />}
                    </Box>
                  }
                  sx={{ m: 0 }}
                />
              </Grid>
            </Grid>

            {/* Vector Search Control */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: 'rgba(255, 248, 240, 0.5)' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={vectorSearchEnabled}
                    onChange={(e) => {
                      setVectorSearchEnabled(e.target.checked);
                      if (!e.target.checked) {
                        setSelectedCategories([]);
                      }
                    }}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">
                      Enable Vector Search - Augment with relevant embeddings
                    </Typography>
                  </Box>
                }
              />
              {vectorSearchEnabled && (
                <Box sx={{ mt: 2 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="category-select-label">Select Categories</InputLabel>
                    <Select
                      labelId="category-select-label"
                      multiple
                      value={selectedCategories}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSelectedCategories(typeof value === 'string' ? value.split(',') : value);
                      }}
                      input={<OutlinedInput label="Select Categories" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip 
                              key={value} 
                              label={categories.find(c => c.id === value)?.name || value}
                              size="small"
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {categories.map((category) => (
                        <MenuItem key={category.id} value={category.id}>
                          <Checkbox checked={selectedCategories.includes(category.id)} />
                          <ListItemText primary={category.name} />
                        </MenuItem>
                      ))}
                      {categories.length === 0 && (
                        <MenuItem disabled>
                          <Typography variant="body2" color="textSecondary">
                            No categories available
                          </Typography>
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                  {selectedCategories.length > 0 && (
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                      Will search top 8 relevant chunks from selected categories
                    </Typography>
                  )}
                </Box>
              )}
            </Paper>


            {/* Code Examples Section */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
                {codeExamples.map((example, index) => (
                  <Tab key={index} label={example.label} />
                ))}
              </Tabs>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Tooltip title="Use for files/binary: sends raw files via multipart/form-data instead of base64 in JSON. Best for Postman, curl, or direct uploads.">
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={formDataMode} 
                        onChange={(e) => setFormDataMode(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontWeight: formDataMode ? 'bold' : 'normal' }}>
                        Form Data Mode — Files &amp; Binary
                      </Typography>
                    }
                  />
                </Tooltip>
              </Box>
            </Box>

            {formDataMode && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  ⚠️ Form Data Mode — Copy &amp; Paste Instructions (files/binary, not base64):
                </Typography>
                <Typography variant="body2" component="div" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                  <strong>Before running the command:</strong><br/>
                  1. Replace file paths with your actual files<br/>
                  2. Use straight quotes (" ") not smart quotes (“ ”)<br/>
                  3. Verify files exist: <code>ls /path/to/your/file</code><br/>
                  <br/>
                  <strong>File Upload Examples:</strong><br/>
                  • Single file: <code>-F "input=@/Users/john/image.jpg"</code><br/>
                  • Multiple files: Use input, input1, input2...<br/>
                  • Text input: <code>-F "input=Your text"</code> (no @)<br/>
                  <br/>
                  <strong>Maximum file sizes:</strong><br/>
                  • Direct upload: Up to 10MB (fast)<br/>
                  • Large files: 10MB+ uploaded to GCS automatically<br/>
                </Typography>
              </Alert>
            )}

            {codeExamples.map((example, index) => (
              <TabPanel key={index} value={tabValue} index={index}>
                <Box sx={{ position: 'relative' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CopyIcon />}
                    onClick={() => handleCopy(example.code)}
                    sx={{ position: 'absolute', top: 0, right: 0, zIndex: 1 }}
                  >
                    Copy
                  </Button>
                  <Box
                    component="pre"
                    sx={{
                      backgroundColor: '#f5f5f5',
                      p: 2,
                      borderRadius: 1,
                      overflow: 'auto',
                      fontSize: '0.9rem',
                      fontFamily: 'monospace',
                      lineHeight: 1.5,
                      maxHeight: '60vh',
                      whiteSpace: 'pre',
                      wordBreak: 'normal',
                      overflowX: 'auto',
                      '& code': {
                        fontFamily: 'monospace',
                        display: 'block',
                        whiteSpace: 'pre'
                      }
                    }}
                  >
                    <code>{example.code}</code>
                  </Box>
                </Box>
              </TabPanel>
            ))}

            {/* Feature Explanation Section - Moved to bottom after code examples */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2, mt: 3, backgroundColor: 'rgba(230, 244, 255, 0.5)' }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                🚀 Platform Features Guide
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  🧵 Thread Mode (Conversation Context)
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>When to use:</strong> Enable for chatbots, multi-turn conversations, or any function that needs to remember previous interactions.
                </Typography>
                <Typography variant="body2" component="div" sx={{ mt: 0.5, fontFamily: 'monospace', fontSize: '0.85rem' }}>
                  • <code>EASYAI-THREAD-ID</code>: Unique identifier for the conversation (use same ID for related requests)<br/>
                  • <code>EASYAI-MAX-THREAD-LEN</code>: Max messages to keep in history (default: 10, max: {maxThreadLength})
                </Typography>
                <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                  💡 The system automatically stores and retrieves conversation history using your thread ID
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                  🔍 Vector Search / Knowledge Base
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>When to use:</strong> Automatically augment requests with relevant information from your knowledge base (uploaded documents, PDFs, etc.).
                </Typography>
                <Typography variant="body2" component="div" sx={{ mt: 0.5, fontFamily: 'monospace', fontSize: '0.85rem' }}>
                  • Searches your uploaded documents for relevant context<br/>
                  • Returns top 8 most relevant chunks by default<br/>
                  • Automatically injected into the AI function's context<br/>
                  • Filter by specific categories for targeted search
                </Typography>
                <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                  💡 Upload documents via the Knowledge Base section, then enable this feature to use them
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                  📊 Usage Tracking Headers (Response)
                </Typography>
                <Typography variant="body2" component="div" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                  • <code>{REQUEST_HEADERS.INPUT_TOKENS}</code>: Input tokens consumed<br/>
                  • <code>{REQUEST_HEADERS.OUTPUT_TOKENS}</code>: Output tokens generated<br/>
                  • <code>{REQUEST_HEADERS.CREDITS_USED}</code>: AI credits charged<br/>
                  • <code>{REQUEST_HEADERS.USAGE}</code>: Total tokens (legacy, for backward compatibility)
                </Typography>
              </Box>
            </Paper>

            {copyAlert && (
              <Alert severity="success" sx={{ position: 'fixed', bottom: 20, right: 20 }}>
                Code copied to clipboard!
              </Alert>
            )}
          </DialogContent>
        </Box>
      ) : (
        <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '10px'}}>
              <Typography variant="h6">Code Examples: {functionName}</Typography>
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {/* Controls */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={2.5}>
                <FormControl fullWidth size="small">
                  <InputLabel>Input Type</InputLabel>
                  <Select value={selectedInputType} onChange={(e) => setSelectedInputType(e.target.value)} label="Input Type">
                    {inputTypes.map(type => (
                      <MenuItem key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2.5}>
                <FormControl fullWidth size="small">
                  <InputLabel>Model</InputLabel>
                  <Select value={selectedModel} onChange={(e) => setSelectedModel(e.target.value)} label="Model">
                    {allowedModels.map(model => (
                      <MenuItem key={model} value={model}>
                        {model}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>API Key</InputLabel>
                  <Select value={selectedApiKey} onChange={(e) => setSelectedApiKey(e.target.value)} label="API Key">
                    <MenuItem value="YOUR_AUTH_TOKEN">YOUR_AUTH_TOKEN</MenuItem>
                    {apiKeys.map(k => (
                      <MenuItem key={k.id} value={k.api_key || ''}>
                        {(k.name || `Key ${k.id}`) + (k.api_key ? ` — ${k.api_key}` : '')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={threadMode} 
                      onChange={(e) => setThreadMode(e.target.checked)}
                      color="primary"
                      size="small"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <span>Thread Mode</span>
                      {threadMode && <Chip label="ON" size="small" color="primary" sx={{ height: 18, fontSize: '0.7rem' }} />}
                    </Box>
                  }
                  sx={{ m: 0 }}
                />
              </Grid>
            </Grid>

            {/* Vector Search Control */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: 'rgba(255, 248, 240, 0.5)' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={vectorSearchEnabled}
                    onChange={(e) => {
                      setVectorSearchEnabled(e.target.checked);
                      if (!e.target.checked) {
                        setSelectedCategories([]);
                      }
                    }}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">
                      Enable Vector Search - Augment with relevant embeddings
                    </Typography>
                  </Box>
                }
              />
              {vectorSearchEnabled && (
                <Box sx={{ mt: 2 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="category-select-label">Select Categories</InputLabel>
                    <Select
                      labelId="category-select-label"
                      multiple
                      value={selectedCategories}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSelectedCategories(typeof value === 'string' ? value.split(',') : value);
                      }}
                      input={<OutlinedInput label="Select Categories" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip 
                              key={value} 
                              label={categories.find(c => c.id === value)?.name || value}
                              size="small"
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {categories.map((category) => (
                        <MenuItem key={category.id} value={category.id}>
                          <Checkbox checked={selectedCategories.includes(category.id)} />
                          <ListItemText primary={category.name} />
                        </MenuItem>
                      ))}
                      {categories.length === 0 && (
                        <MenuItem disabled>
                          <Typography variant="body2" color="textSecondary">
                            No categories available
                          </Typography>
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                  {selectedCategories.length > 0 && (
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                      Will search top 8 relevant chunks from selected categories
                    </Typography>
                  )}
                </Box>
              )}
            </Paper>


            {/* Code Examples Section */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
                {codeExamples.map((example, index) => (
                  <Tab key={index} label={example.label} />
                ))}
              </Tabs>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Tooltip title="Use for files/binary: sends raw files via multipart/form-data instead of base64 in JSON. Best for Postman, curl, or direct uploads.">
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={formDataMode} 
                        onChange={(e) => setFormDataMode(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontWeight: formDataMode ? 'bold' : 'normal' }}>
                        Form Data Mode — Files &amp; Binary
                      </Typography>
                    }
                  />
                </Tooltip>
              </Box>
            </Box>

            {formDataMode && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  ⚠️ Form Data Mode — Copy &amp; Paste Instructions (files/binary, not base64):
                </Typography>
                <Typography variant="body2" component="div" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                  <strong>Before running the command:</strong><br/>
                  1. Replace file paths with your actual files<br/>
                  2. Use straight quotes (" ") not smart quotes (“ ”)<br/>
                  3. Verify files exist: <code>ls /path/to/your/file</code><br/>
                  <br/>
                  <strong>File Upload Examples:</strong><br/>
                  • Single file: <code>-F "input=@/Users/john/image.jpg"</code><br/>
                  • Multiple files: Use input, input1, input2...<br/>
                  • Text input: <code>-F "input=Your text"</code> (no @)<br/>
                  <br/>
                  <strong>Maximum file sizes:</strong><br/>
                  • Direct upload: Up to 10MB (fast)<br/>
                  • Large files: 10MB+ uploaded to GCS automatically<br/>
                </Typography>
              </Alert>
            )}

            {codeExamples.map((example, index) => (
              <TabPanel key={index} value={tabValue} index={index}>
                <Box sx={{ position: 'relative' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CopyIcon />}
                    onClick={() => handleCopy(example.code)}
                    sx={{ position: 'absolute', top: 0, right: 0, zIndex: 1 }}
                  >
                    Copy
                  </Button>
                  <Box
                    component="pre"
                    sx={{
                      backgroundColor: '#f5f5f5',
                      p: 2,
                      borderRadius: 1,
                      overflow: 'auto',
                      fontSize: '0.9rem',
                      fontFamily: 'monospace',
                      lineHeight: 1.5,
                      maxHeight: '60vh',
                      whiteSpace: 'pre',
                      wordBreak: 'normal',
                      overflowX: 'auto',
                      '& code': {
                        fontFamily: 'monospace',
                        display: 'block',
                        whiteSpace: 'pre'
                      }
                    }}
                  >
                    <code>{example.code}</code>
                  </Box>
                </Box>
              </TabPanel>
            ))}

            {/* Feature Explanation Section - Moved to bottom after code examples */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2, mt: 3, backgroundColor: 'rgba(230, 244, 255, 0.5)' }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                🚀 Platform Features Guide
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  🧵 Thread Mode (Conversation Context)
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>When to use:</strong> Enable for chatbots, multi-turn conversations, or any function that needs to remember previous interactions.
                </Typography>
                <Typography variant="body2" component="div" sx={{ mt: 0.5, fontFamily: 'monospace', fontSize: '0.85rem' }}>
                  • <code>EASYAI-THREAD-ID</code>: Unique identifier for the conversation (use same ID for related requests)<br/>
                  • <code>EASYAI-MAX-THREAD-LEN</code>: Max messages to keep in history (default: 10, max: {maxThreadLength})
                </Typography>
                <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                  💡 The system automatically stores and retrieves conversation history using your thread ID
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                  🔍 Vector Search / Knowledge Base
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>When to use:</strong> Automatically augment requests with relevant information from your knowledge base (uploaded documents, PDFs, etc.).
                </Typography>
                <Typography variant="body2" component="div" sx={{ mt: 0.5, fontFamily: 'monospace', fontSize: '0.85rem' }}>
                  • Searches your uploaded documents for relevant context<br/>
                  • Returns top 8 most relevant chunks by default<br/>
                  • Automatically injected into the AI function's context<br/>
                  • Filter by specific categories for targeted search
                </Typography>
                <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                  💡 Upload documents via the Knowledge Base section, then enable this feature to use them
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                  📊 Usage Tracking Headers (Response)
                </Typography>
                <Typography variant="body2" component="div" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                  • <code>{REQUEST_HEADERS.INPUT_TOKENS}</code>: Input tokens consumed<br/>
                  • <code>{REQUEST_HEADERS.OUTPUT_TOKENS}</code>: Output tokens generated<br/>
                  • <code>{REQUEST_HEADERS.CREDITS_USED}</code>: AI credits charged<br/>
                  • <code>{REQUEST_HEADERS.USAGE}</code>: Total tokens (legacy, for backward compatibility)
                </Typography>
              </Box>
            </Paper>

            {copyAlert && (
              <Alert severity="success" sx={{ position: 'fixed', bottom: 20, right: 20 }}>
                Code copied to clipboard!
              </Alert>
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};